import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  message,
  Space,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  useThicknessGaugesQuery,
  useCreateThicknessGaugeMutation,
  useUpdateThicknessGaugeMutation,
  useDeleteThicknessGaugeMutation,
} from "../../Services";
import { ThicknessGauge } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface ThicknessGaugeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newGauge?: ThicknessGauge) => void;
}

export const ThicknessGaugeModal: React.FC<ThicknessGaugeModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: thicknessGauges = [], isLoading } = useThicknessGaugesQuery({
    enabled: open,
  });

  const createMutation = useCreateThicknessGaugeMutation();
  const updateMutation = useUpdateThicknessGaugeMutation();
  const deleteMutation = useDeleteThicknessGaugeMutation();

  const handleStartEdit = (record: ThicknessGauge) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      thickness_mm: record.thickness_mm,
      gauge_val: record.gauge_val || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        updateMutation.mutate(
          {
            id: editingId,
            name: values.name.trim(),
            thickness_mm: values.thickness_mm,
            gauge_val: values.gauge_val ? values.gauge_val.trim() : undefined,
          },
          {
            onSuccess: (res: any) => {
              if (res?.success) {
                message.success("Thickness gauge updated successfully!");
                handleCancelEdit();
              } else {
                message.error(res?.message || "Failed to update thickness gauge.");
              }
            },
            onError: () => message.error("Something went wrong."),
          },
        );
      } else {
        createMutation.mutate(values, {
          onSuccess: (res: any) => {
            if (res?.success) {
              message.success("Thickness gauge config added successfully!");
              form.resetFields();
              if (onSuccess) {
                onSuccess(res?.data);
              }
            } else {
              message.error(res?.message || "Failed to add thickness gauge.");
            }
          },
          onError: () => message.error("Something went wrong."),
        });
      }
    } catch {
      message.error("Please enter valid thickness gauge details.");
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res: any) => {
        if (res?.success) {
          message.success("Thickness gauge deleted successfully!");
          if (editingId === id) {
            handleCancelEdit();
          }
        } else {
          message.error(res?.message || "Failed to delete thickness gauge.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const handleModalClose = () => {
    handleCancelEdit();
    onClose();
  };

  const columns = [
    {
      title: "Config Name / Label",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: ThicknessGauge) => (
        <span
          className={`font-semibold text-xs ${
            record.id === editingId ? "text-blue-600 font-bold" : "text-gray-800"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Thickness (mm)",
      dataIndex: "thickness_mm",
      key: "thickness_mm",
      width: 120,
      render: (val: number) => (
        <span className="font-mono text-xs font-bold text-blue-700">
          {val} mm
        </span>
      ),
    },
    {
      title: "Gauge Standard",
      dataIndex: "gauge_val",
      key: "gauge_val",
      width: 120,
      render: (val: string) => (
        <span className="font-mono text-xs">{val || "—"}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center" as const,
      render: (_: any, record: ThicknessGauge) => (
        <Space size="small">
          <Button
            type="text"
            icon={
              <EditOutlined
                className={record.id === editingId ? "text-blue-600" : ""}
              />
            }
            size="small"
            onClick={() => handleStartEdit(record)}
          />
          <Popconfirm
            title="Delete this gauge config?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Thickness / Gauge Configurations")}
      open={open}
      onCancel={handleModalClose}
      footer={modalFooter({
        actions: [{ label: "Close", onClick: handleModalClose }],
      })}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        className="my-3 bg-slate-50 border border-slate-200 p-3 rounded-lg"
      >
        <div className="grid grid-cols-3 gap-3">
          <Form.Item
            name="name"
            label="Gauge Label / Name"
            rules={[{ required: true, message: "Label is required" }]}
            className="mb-0"
          >
            <Input placeholder="e.g. 0.47 mm (28 Gauge)" prefix={<PlusOutlined />} />
          </Form.Item>

          <Form.Item
            name="thickness_mm"
            label="Thickness (mm)"
            rules={[{ required: true, message: "Thickness is required" }]}
            className="mb-0"
          >
            <InputNumber className="w-full" placeholder="0.47" min={0} step={0.01} />
          </Form.Item>

          <Form.Item name="gauge_val" label="Gauge (Optional)" className="mb-0">
            <Input placeholder="e.g. 28 Gauge" />
          </Form.Item>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <Button size="middle" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            size="middle"
            onClick={handleFormSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            className={
              editingId
                ? "bg-blue-600 hover:bg-blue-700 font-semibold"
                : "bg-emerald-600 hover:bg-emerald-700 font-semibold"
            }
          >
            {editingId ? "Update" : "Add"}
          </Button>
        </div>
      </Form>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={thicknessGauges}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </Modal>
  );
};

